import React, { createContext, useContext, useEffect, useState } from 'react';

interface SafeAreasContextValue {
  safeAreas: {
    top: number;
    bottom: number;
    left: number;
    right: number;
  };
  isReady: boolean;
}

const SafeAreasContext = createContext<SafeAreasContextValue | undefined>(undefined);

const parsePxValue = (value: string): number => {
  if (!value || value === '0') return 0;
  const parsed = parseFloat(value.replace('px', ''));
  return isNaN(parsed) ? 0 : Math.round(parsed);
};

const getSafeAreaInset = (edge: 'top' | 'right' | 'bottom' | 'left'): number => {
  try {
    const style = getComputedStyle(document.documentElement);
    // Try to read from the CSS custom property first
    const customProp = style.getPropertyValue(`--safe-area-inset-${edge}`).trim();
    if (customProp && customProp !== '0') {
      return parsePxValue(customProp);
    }

    // Fallback: create a temporary element to measure env() value
    const testDiv = document.createElement('div');
    testDiv.style.position = 'fixed';
    testDiv.style.top = '-9999px';
    testDiv.style.padding = `env(safe-area-inset-${edge})`;
    document.body.appendChild(testDiv);

    const computedPadding = window.getComputedStyle(testDiv).paddingTop;
    document.body.removeChild(testDiv);

    return parsePxValue(computedPadding);
  } catch (error) {
    console.warn(`Failed to get safe area inset for ${edge}:`, error);
    return 0;
  }
};

export function SafeAreasProvider({ children }: { children: React.ReactNode }) {
  const [safeAreas, setSafeAreas] = useState({
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
  });
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const detectSafeAreas = () => {
      if (typeof window !== 'undefined') {
        setSafeAreas({
          top: getSafeAreaInset('top'),
          bottom: getSafeAreaInset('bottom'),
          left: getSafeAreaInset('left'),
          right: getSafeAreaInset('right'),
        });
        setIsReady(true);
      }
    };

    // Initial detection with a small delay
    const timeoutId = setTimeout(detectSafeAreas, 100);

    window.addEventListener('resize', detectSafeAreas);
    window.addEventListener('orientationchange', detectSafeAreas);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', detectSafeAreas);
      window.removeEventListener('orientationchange', detectSafeAreas);
    };
  }, []);

  return (
    <SafeAreasContext.Provider value={{ safeAreas, isReady }}>
      {children}
    </SafeAreasContext.Provider>
  );
}

export function useSafeAreas() {
  const context = useContext(SafeAreasContext);
  if (context === undefined) {
    throw new Error('useSafeAreas deve ser usado dentro de um SafeAreasProvider');
  }
  return context;
}

// Alias para compatibilidade
export const useSafeArea = useSafeAreas;